/** What a session is paid, per kind of session. Null is "never set", not zero. */
export interface PayRates {
    individual: string | null;
    couple: string | null;
    group: string | null;
    /** A group's rate by its number of members, keyed "2" to "20". */
    group_sizes: Record<string, string | null>;
}

export type PayRateType = "individual" | "couple" | "group";

/** The group sizes that can have a rate of their own. */
export const MIN_GROUP_SIZE = 2;
export const MAX_GROUP_SIZE = 20;
export const GROUP_SIZES: number[] = Array.from(
    { length: MAX_GROUP_SIZE - MIN_GROUP_SIZE + 1 },
    (_, index) => MIN_GROUP_SIZE + index
);

/** How many group sizes have a rate of their own. */
export function sizesSet(rates: PayRates): number {
    return Object.values(rates.group_sizes ?? {}).filter((value) => value !== null && value !== "").length;
}

export const PAY_RATE_TYPES: PayRateType[] = ["individual", "couple", "group"];

export const EMPTY_RATES: PayRates = { individual: null, couple: null, group: null, group_sizes: {} };

export interface CentreRates {
    center: PayRates;
    overrides: { therapist_id: string; rates: PayRates }[];
}

export interface PractitionerRates {
    own: PayRates;
    center: { center_id: string; center_name: string | null; effective: PayRates } | null;
}

export function hasAnyRate(rates: PayRates): boolean {
    return PAY_RATE_TYPES.some((type) => rates[type] !== null) || sizesSet(rates) > 0;
}

const MAX_RATE = 100000;

export type RateError = "invalid" | "negative" | "tooLarge" | "decimals";

/**
 * What is wrong with an amount as typed, or null when it is fine. Empty is
 * fine: it leaves the rate unset. A comma is read as the decimal point, as
 * a Spanish keyboard writes it.
 */
export function validateRate(input: string): RateError | null {
    const value = input.trim().replace(",", ".");
    if (value === "") return null;
    if (!/^-?\d+(\.\d+)?$/.test(value)) return "invalid";
    const amount = Number(value);
    if (amount < 0) return "negative";
    if (amount > MAX_RATE) return "tooLarge";
    if ((value.split(".")[1] ?? "").length > 2) return "decimals";
    return null;
}

/** An amount as typed, as the API takes it: a decimal string, or null when empty. */
export function normalizeRate(input: string): string | null {
    const value = input.trim().replace(",", ".");
    return value === "" ? null : value;
}
